const configCache = new Map();

const request = async (pathname, payload) => {
  const response = await fetch(pathname, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  let data = null;

  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }

  if (!response.ok || !data?.ok) {
    const requestError = new Error(
      data?.error || "Не удалось связаться с Wazzup. Повторите попытку"
    );
    requestError.status = response.status;
    throw requestError;
  }

  return data;
};

const getCacheKey = (auth = {}) =>
  `${auth.provider || "crm"}:${auth.accountId || auth.publicToken || auth.member_id || auth.domain || "current"}`;

const getConfig = async (auth, { force = false } = {}) => {
  const cacheKey = getCacheKey(auth);

  if (!force && configCache.has(cacheKey)) {
    return configCache.get(cacheKey);
  }

  const config = await request("/api/wazzup/config", auth);
  configCache.set(cacheKey, config);
  return config;
};

const withSession = async (auth, pathname, payload = {}) => {
  let config = await getConfig(auth);

  try {
    return await request(pathname, {
      sessionToken: config.sessionToken,
      ...payload,
    });
  } catch (error) {
    if (error.status !== 401) {
      throw error;
    }

    config = await getConfig(auth, { force: true });
    return request(pathname, {
      sessionToken: config.sessionToken,
      ...payload,
    });
  }
};

const updateCache = (auth, config) => {
  configCache.set(getCacheKey(auth), config);
  return config;
};

const mergeDayEvents = ({
  crmEvents = {},
  wazzupData = {},
  isCrmMessageEvent,
}) => {
  const result = Object.fromEntries(
    Object.entries(crmEvents || {}).map(([userId, events]) => [
      userId,
      Array.isArray(events) ? [...events] : [],
    ])
  );
  const preferences = Object.fromEntries(
    (wazzupData.mappings || []).map((mapping) => [String(mapping.userId), mapping])
  );

  Object.entries(preferences).forEach(([userId, mapping]) => {
    const directEvents = (wazzupData.events?.[userId] || []).filter(Boolean);
    const directTimes = directEvents.map((event) => Number(event.messageAt || event.startAt));
    const currentEvents = result[userId] || [];

    result[userId] = currentEvents.filter((event) => {
      if (!isCrmMessageEvent(event)) {
        return true;
      }

      if (!mapping.includeCrm) {
        return false;
      }

      if (!mapping.includeWazzup || !directTimes.length) {
        return true;
      }

      if (event.source === "wazzup_timeline_comment") {
        return false;
      }

      const eventTime = Number(event.messageAt || event.originalStartAt || event.startAt);
      return !directTimes.some((directTime) => Math.abs(directTime - eventTime) <= 90);
    });

    if (mapping.includeWazzup && directEvents.length) {
      result[userId].push(...directEvents);
      result[userId].sort((first, second) => first.startAt - second.startAt);
    }
  });

  return result;
};

export default {
  getConfig,

  async connect(auth, apiKey) {
    const config = await withSession(auth, "/api/wazzup/connect", { apiKey });
    return updateCache(auth, config);
  },

  async saveMappings(auth, mappings) {
    const config = await withSession(auth, "/api/wazzup/mappings", { mappings });
    return updateCache(auth, config);
  },

  async getDay(auth, day, timezone) {
    const config = await getConfig(auth);

    if (!config.connected) {
      return { connected: false, events: {}, mappings: config.mappings || [] };
    }

    return withSession(auth, "/api/wazzup/day", { day, timezone });
  },

  async getDaySafe(auth, day, timezone) {
    try {
      return await this.getDay(auth, day, timezone);
    } catch (error) {
      console.warn("Wazzup events are temporarily unavailable", error);
      const cachedConfig = configCache.get(getCacheKey(auth));

      return {
        connected: false,
        events: {},
        mappings: cachedConfig?.mappings || [],
      };
    }
  },

  mergeDayEvents,
};
